import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { cellText } from "../utils/firestoreValueUtils";
import { toMillis } from "./explorerFilterUtils";
import HighlightedText from "./HighlightedText";

// MIT DataGrid caps client page size at 100 (larger requires DataGridPro).
const GRID_PAGE_SIZES = [25, 50, 100];

/** Resolve the ordered, pinned field list from saved column preferences. */
function resolveOrderedFields(fields, prefs) {
  const saved = (prefs.order || []).filter((f) => fields.includes(f));
  const missing = fields.filter((f) => !saved.includes(f));
  const all = [...saved, ...missing];
  const left = (prefs.pinnedLeft || []).filter((f) => all.includes(f));
  const right = (prefs.pinnedRight || []).filter((f) => all.includes(f));
  const middle = all.filter((f) => !left.includes(f) && !right.includes(f));
  return [...left, ...middle, ...right];
}

export default function EnterpriseDataGrid({
  apiRef,
  rows,
  fields,
  fieldTypes,
  prefs,
  onPrefsChange,
  searchTerm,
  selectedIds,
  onSelectionChange,
  onRowOpen,
  onRowEdit,
  loading,
  highlightIds,
}) {
  const orderedFields = useMemo(() => resolveOrderedFields(fields, prefs), [fields, prefs]);

  const getRowClassName = useMemo(() => {
    if (!highlightIds || !highlightIds.size) return undefined;
    return (params) => (highlightIds.has(params.id) ? "fa-highlight-row" : "");
  }, [highlightIds]);

  const columns = useMemo(
    () =>
      orderedFields.map((field) => {
        const type = fieldTypes[field] || "string";
        return {
          field,
          headerName: field === "id" ? "Document ID" : field,
          minWidth: 130,
          ...(prefs.widths?.[field] ? { width: prefs.widths[field] } : { flex: 1 }),
          sortable: true,
          valueGetter: (_value, row) => {
            const raw = field === "id" ? row.id : row?.data?.[field];
            if (type === "timestamp") return toMillis(raw) ?? 0;
            if (type === "number") return typeof raw === "number" ? raw : Number(raw) || 0;
            return cellText(raw);
          },
          renderCell: (params) => {
            const raw = field === "id" ? params.row.id : params.row?.data?.[field];
            return <HighlightedText text={cellText(raw)} term={searchTerm} />;
          },
        };
      }),
    [orderedFields, fieldTypes, prefs.widths, searchTerm]
  );

  const columnVisibilityModel = useMemo(() => {
    const model = {};
    fields.forEach((field) => {
      if (prefs.hidden?.[field]) model[field] = false;
    });
    return model;
  }, [fields, prefs.hidden]);

  const rowSelectionModel = useMemo(
    () => ({ type: "include", ids: selectedIds || new Set() }),
    [selectedIds]
  );

  const handleSelectionChange = (model) => {
    if (model.type === "exclude") {
      const next = new Set(rows.map((r) => r.id).filter((id) => !model.ids.has(id)));
      onSelectionChange(next);
    } else {
      onSelectionChange(new Set(model.ids));
    }
  };

  return (
    <Box sx={{ width: "100%", height: { xs: 540, md: 660 } }}>
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleSelectionChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          const hidden = {};
          fields.forEach((field) => {
            if (model[field] === false) hidden[field] = true;
          });
          onPrefsChange({ ...prefs, hidden });
        }}
        onColumnOrderChange={() => {
          if (!apiRef?.current) return;
          const order = apiRef.current
            .getAllColumns()
            .map((c) => c.field)
            .filter((f) => f !== "__check__" && fields.includes(f));
          onPrefsChange({ ...prefs, order });
        }}
        onColumnWidthChange={(params) => {
          const field = params.colDef?.field;
          if (!field) return;
          onPrefsChange({ ...prefs, widths: { ...(prefs.widths || {}), [field]: Math.round(params.width) } });
        }}
        density={prefs.density || "standard"}
        onDensityChange={(density) => onPrefsChange({ ...prefs, density })}
        onRowClick={(params) => onRowOpen(params.row)}
        onRowDoubleClick={(params) => onRowEdit(params.row)}
        getRowClassName={getRowClassName}
        pagination
        pageSizeOptions={GRID_PAGE_SIZES}
        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
        showToolbar
        sx={{
          "& .MuiDataGrid-columnHeaders": { position: "sticky", top: 0 },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .fa-highlight-row": {
            bgcolor: "rgba(239,68,68,0.14)",
            "&:hover": { bgcolor: "rgba(239,68,68,0.22)" },
          },
        }}
      />
    </Box>
  );
}
